// byClaude-start 2026-07-30 启动瘦身：重型 SDK + App 延迟到 render 后异步加载
import i18next from 'i18next';
import store, { persistor } from '@web/common/src/store';
import { nativeApi } from '@web/common/src/utils/nativeApi';
import { sensorsTrack } from '@web/common/src/utils/shenceTrack';
import { isDev } from '@web/common/src/utils/util';
import { parse, stringify } from 'query-string';
import React, { StrictMode } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter as Router } from 'react-router-dom';
import App from './App';
import { PersistGate } from 'redux-persist/integration/react';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import './styles/index.less';
import { initLanguage } from './utils/languageutils';
import { registerVhkUnauthorizedHandler } from './utils/registerVhkUnauthorizedHandler';
import { VHKUnhandleRejection } from './utils/unhandleRejection';
import { persistor as vhkPersistor } from './vhkstore';

// ===== 首屏必需：尽早执行 =====
nativeApi.init?.({ initLangCallBack: initLanguage });
registerVhkUnauthorizedHandler();

VHKUnhandleRejection();

document.body.className = `${document.body.className} ${i18next.language === 'en' ? 'en' : ''}`;

// App 同步导入：确保首屏路由不等待额外 chunk（Intro 作为入口需要秒开）

// ===== 首屏渲染：仅 React 全家桶 + Redux，骨架屏自然过渡到 App =====
ReactDOM.render(
  <StrictMode>
    <Router>
      <QueryParamProvider
        adapter={ReactRouter6Adapter}
        options={{ searchStringToObject: parse, objectToSearchString: stringify }}
      >
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <PersistGate loading={null} persistor={vhkPersistor}>
              <App />
            </PersistGate>
          </PersistGate>
        </Provider>
      </QueryParamProvider>
    </Router>
  </StrictMode>,
  document.querySelector('#root')
);

// ===== 渲染后初始化（不阻塞首屏） =====

// 神策埋点：render 后立即同步初始化（App.tsx 的 useEffect 依赖 sensors 全局变量）
sensorsTrack(import.meta.env.VITE_APP_SHENCE_SERVER_URL as string);

// 重型 SDK 延迟到浏览器空闲时加载
const loadDeferredSDKs = () => {
  // dayjs 中文 locale（侧效应 import）
  import('dayjs/locale/zh-hk.js');

  // Sentry 错误监控
  if (!isDev() && String(import.meta.env.VITE_APP_STARTSENTRY) === 'true') {
    import('./sentryInit').then((m) => m.initSentry());
  }
};

if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(loadDeferredSDKs, { timeout: 2000 });
} else {
  setTimeout(loadDeferredSDKs, 200);
}

// byClaude-start 2026-08-03 15:30:00 追踪 SDK 延迟到首屏稳定后加载：load → idle → 或用户首次交互时提前触发
if (!isDev()) {
  const addLoadEvent = (fun: () => void) => {
    const oldLoad = window.onload as ((arg: unknown) => void) | null;
    if (typeof oldLoad !== 'function') {
      window.onload = fun;
    } else {
      window.onload = (arg: unknown) => {
        oldLoad && oldLoad(arg);
        fun();
      };
    }
  };

  let trackingInitPending = false;
  const initTrackingLazy = () => {
    if (trackingInitPending) return;
    trackingInitPending = true;
    import('./trackingInit').then((m) => m.initTracking());
  };

  addLoadEvent(() => {
    // load 后延迟 5 秒加载追踪 SDK，让浏览器优先处理首屏渲染和核心 API
    setTimeout(initTrackingLazy, 5000);

    // 用户首次交互时提前触发（避免丢失追踪）
    const earlyTrigger = () => {
      initTrackingLazy();
      window.removeEventListener('click', earlyTrigger);
      window.removeEventListener('touchstart', earlyTrigger);
      window.removeEventListener('scroll', earlyTrigger);
    };
    window.addEventListener('click', earlyTrigger, { once: true, passive: true });
    window.addEventListener('touchstart', earlyTrigger, { once: true, passive: true });
    window.addEventListener('scroll', earlyTrigger, { once: true, passive: true });
  });
}
// byClaude-end 2026-08-03 15:30:00
// byClaude-end 2026-07-30
// Contains claude-generated edits.
